// Member-profile privacy.
//
// Each member.privacy is an object keyed by field name with a boolean value:
//   true  → the field is visible to everyone
//   false → the field is hidden from everyone EXCEPT the always-allowed
//           viewers: Super Admin, Limited Admin, the member's assigned Pastor,
//           and the member themselves.
//
// Defaults: all fields are visible BY DEFAULT, except `birthYear` which is
// hidden by default. When a member record has no `privacy` block (legacy
// records or new members) we apply these defaults transparently.

export const PRIVACY_FIELDS = [
  { key: "birthYear",       label: "Birth Year",                hint: "Month and day always visible — only the year is hidden." },
  { key: "gender",          label: "Gender",                    hint: "" },
  { key: "maritalStatus",   label: "Marital Status",            hint: "" },
  { key: "language",        label: "Language",                  hint: "" },
  { key: "profession",      label: "Profession & Details",      hint: "Covers both Profession and Profession Details." },
  { key: "mobilePhone",     label: "Mobile Phone",              hint: "Hides every phone number on your profile." },
  { key: "personalEmail",   label: "Personal Email",            hint: "Hides every email address on your profile." },
  { key: "physicalAddress", label: "Physical Address",          hint: "" },
  { key: "baptism",         label: "Baptism Status & Details",  hint: "Hides 'Baptized' plus 'Baptized By', 'Where' and 'Date'." },
  { key: "invitedBy",       label: "Invited By",                hint: "" },
  { key: "additionType",    label: "Addition Type",             hint: "" },
];

// `photo` is a special privacy field — it ONLY appears in the privacy editor
// when the super-admin has explicitly turned on the global setting
// `allowPhotoPrivacy` in kc_app_settings. Default is OFF (photos always
// visible). Even when ON, super-admin / limited-admin / assigned pastor
// continue to see photos regardless of the per-member toggle.
export const OPTIONAL_PHOTO_PRIVACY_FIELD = { key: "photo", label: "Profile Photo", hint: "Hides your photo from non-privileged viewers. Pastors and admins always see your photo." };

export function privacyFieldsForSettings(appSettings) {
  if (appSettings && appSettings.allowPhotoPrivacy) {
    return [OPTIONAL_PHOTO_PRIVACY_FIELD, ...PRIVACY_FIELDS];
  }
  return PRIVACY_FIELDS;
}

export const PRIVACY_KEYS = PRIVACY_FIELDS.map(f => f.key);
// All keys we may persist on a member (privacy_fields + optional photo).
const ALL_PRIVACY_KEYS = [...PRIVACY_KEYS, OPTIONAL_PHOTO_PRIVACY_FIELD.key];

// Defaults — every field visible (true) except birthYear which is hidden.
export const PRIVACY_DEFAULTS = Object.freeze(
  PRIVACY_FIELDS.reduce((acc, f) => { acc[f.key] = f.key !== "birthYear"; return acc; }, { [OPTIONAL_PHOTO_PRIVACY_FIELD.key]: true })
);

// Merge a member's privacy block with the defaults. Missing keys fall back to
// the default value. The function never mutates the input.
export function getPrivacy(member) {
  const merged = { ...PRIVACY_DEFAULTS };
  if (member && member.privacy && typeof member.privacy === "object") {
    for (const k of ALL_PRIVACY_KEYS) {
      if (typeof member.privacy[k] === "boolean") merged[k] = member.privacy[k];
    }
  }
  return merged;
}

// Always-allowed viewers (return true regardless of privacy):
//   - Super Admin (any of them)
//   - Limited Admin (any of them)
//   - The member themselves (linked user)
//   - The Pastor whose `assignedMembers` includes this member's id
// `users` is the full users list — passed in so we can resolve whether the
// viewer is the linked user, and (for pastors) whether the member is
// in their assigned list.
// `appSettings` (optional) — when provided and `fieldKey === "photo"`, the
// global `allowPhotoPrivacy` flag is honoured. If the flag is OFF (default),
// profile photos are ALWAYS visible to every logged-in viewer regardless of
// the per-member privacy block.
export function canViewMemberField(viewer, member, fieldKey, users, appSettings) {
  if (!viewer || !member) return false;
  const role = viewer.role;
  // Photo special case: when the global "allow photo privacy" toggle is OFF
  // (default), every logged-in user can see the photo regardless of any
  // stored privacy.photo value.
  if (fieldKey === "photo" && !(appSettings && appSettings.allowPhotoPrivacy)) {
    return true;
  }
  // Admins always see everything.
  if (role === "superadmin" || role === "limited_admin") return true;
  // The member themselves (the user linked to this member record) sees
  // every field on their own profile.
  if (viewer.memberId && viewer.memberId === member.id) return true;
  // Pastors: visible if they have this member assigned.
  if (role === "pastor" && Array.isArray(viewer.assignedMembers) && viewer.assignedMembers.includes(member.id)) return true;
  // Everyone else: defer to the member's own privacy setting.
  // (Team Members who are NOT assigned to this member, plus all other
  // logged-in users, fall through to this branch.)
  const privacy = getPrivacy(member);
  return privacy[fieldKey] !== false;
}

// Whether `viewer` is one of the privileged roles that can ALWAYS see hidden
// fields (used to render the "Visible to admins / your pastor only" hint
// next to fields the owner has hidden but the privileged viewer is still
// seeing). NOT shown to the member themselves — they always see all fields.
export function isOverrideViewer(viewer, member) {
  if (!viewer || !member) return false;
  if (viewer.role === "superadmin" || viewer.role === "limited_admin") return true;
  if (viewer.role === "pastor" && Array.isArray(viewer.assignedMembers) && viewer.assignedMembers.includes(member.id)) return true;
  return false;
}
